import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import Layout from '@shared/components/Layout';
import { LoginError } from '@shared/components/LoginError';
import bannerSignupImage from '../images/banner-signup-desktop.webp';

type PasskeyDecisionErrorScreenProps = {
  onRetry: () => void;
} & (
  | {
      isPostLogin: true;
      loginHref?: never;
      onContinue: () => boolean;
    }
  | {
      isPostLogin?: false;
      loginHref: string;
      onContinue?: () => boolean;
    }
);

const PasskeyDecisionErrorScreen = (
  props: PasskeyDecisionErrorScreenProps
): React.JSX.Element => {
  const { t } = useTranslation();
  const retryStartedRef = useRef(false);
  const [retryStarted, setRetryStarted] = useState(false);
  const continueStartedRef = useRef(false);
  const [continueStarted, setContinueStarted] = useState(false);
  const isPostLogin = props.isPostLogin === true;

  const retry = (): void => {
    if (retryStartedRef.current) {
      return;
    }

    retryStartedRef.current = true;
    setRetryStarted(true);
    props.onRetry();
  };

  const continuePostLogin = (): void => {
    if (
      props.isPostLogin !== true ||
      continueStartedRef.current ||
      !props.onContinue()
    ) {
      return;
    }

    continueStartedRef.current = true;
    setContinueStarted(true);
  };

  return (
    <Layout
      heading={t('Something went wrong')}
      subHeading={
        isPostLogin
          ? t('Try again, or continue to finish logging in without a passkey.')
          : t(
              'Your account was created. Try again to finish, or continue to log in.'
            )
      }
      variant="banner"
      bannerImageDesktop={bannerSignupImage}
    >
      <div className="space-y-4">
        <LoginError
          pageError={{
            error: t('Something went wrong. Please try again.'),
          }}
        />
        <Button
          label={t('Try again')}
          variant="primary"
          disabled={retryStarted}
          state={retryStarted ? 'loading' : 'default'}
          onClick={retry}
        />
        <div className="text-center">
          {isPostLogin ? (
            <AuthLink>
              <button
                type="button"
                disabled={continueStarted}
                aria-busy={continueStarted}
                onClick={continuePostLogin}
              >
                {t('Continue')}
              </button>
            </AuthLink>
          ) : (
            <AuthLink>
              <a
                href={props.loginHref}
                onClick={event => {
                  // A working native bridge owns the handoff. Missing or stale
                  // bridges return false and retain the normal login fallback.
                  if (props.onContinue?.()) {
                    event.preventDefault();
                  }
                }}
              >
                {t('Continue')}
              </a>
            </AuthLink>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PasskeyDecisionErrorScreen;
